import {
  keepUnlessEditorTruncation,
  percentFormatter,
  percentScaledValueParser,
  percentUnitValueParser
} from "@appretio/ui-kit";
import type {
  ColDef,
  ColGroupDef,
  ValueParserParams
} from "ag-grid-enterprise";
import { ChangeFlashCell } from "../../components/cubeDataTable/cellRenderers/ChangeFlashCell";
import {
  INDEX_BASE,
  isIndexFamilyMode,
  VARIANCE_DECIMALS
} from "../../components/cubeDataTable/utils/indexUtils";
import {
  PER_SQM_SUFFIX,
  STORE_SQM_METRIC
} from "../../components/cubeDataTable/utils/metricConstants";
import type { ForecastTableControlValues } from "../../components/tableControls/forecastTableControlValues";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import {
  formatDecimals,
  isPercentageMetric,
  percentageDecimals
} from "../percentageMetrics";
import {
  INTAKE_COMPONENT_METRICS,
  NON_SUMMABLE_MARKDOWN_METRICS
} from "./salesKpiDerivations";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Temporal aggregation view mode. */
export type AggView = "PTD" | "PTG" | "TTL" | "ALL";

/** Plan-by mode for grid display. One list, shared with the temporal grid. */
export type PlanByMode = ForecastTableControlValues["planBy"];

/** Underlying KPI category as reported by the backend metricCategoryMapping. */
export type MetricCategory = "Value" | "Price" | "Units";

/**
 * View options on the Forecast Summary page. Extends the underlying
 * `MetricCategory` with `Compact` and `Per SQM` — modes that bypass the
 * category-mapping filter and instead show a curated KPI list. Matches
 * dna-fpm-frontend `FCST_SUMM_KPI_VIEW_OPTIONS`.
 */
export type SummaryView = MetricCategory | "Compact" | "Per SQM";

/**
 * Metrics always hidden on the Forecast Summary, regardless of view. Mirrors
 * dna-fpm-frontend `forecast_summary/table.py:EXCLUDED_METRICS` (the "requested
 * by Paul" set): BOP at all unit/cost/AUC variants plus their per-sqm forms.
 */
const EXCLUDED_METRICS = new Set([
  "BEGINNING_OF_PERIOD_UNITS",
  "BEGINNING_OF_PERIOD_UNITS_PER_SQM",
  "BEGINNING_OF_PERIOD_AUC",
  "BEGINNING_OF_PERIOD_AT_COST",
  "BEGINNING_OF_PERIOD_AT_COST_PER_SQM"
]);

/**
 * Most FURTHER_MD_* metrics not safely summable on aggregated views. The Forecast
 * Summary is always aggregated (PTD/PTG/TTL), so these are always hidden. Shared with
 * the temporal grid's aggregate-column blanking (see * NON_SUMMABLE_MARKDOWN_METRICS).
 */
const AGGREGATED_UNSAFE_METRICS = new Set(NON_SUMMABLE_MARKDOWN_METRICS);

/**
 * Editable on the weekly grid, read-only here. Target stock and FPOS derive
 * from each other through a forward scan of weekly sales, which the period
 * columns cannot run, and the summary save persists sales metrics only.
 */
const SUMMARY_READ_ONLY_METRICS = new Set([
  "TARGET_STOCK_UNITS_TOTAL",
  "TARGET_STOCK_AT_COST",
  "TARGET_STOCK_AUC",
  "FORWARD_PERIOD_OF_SALES",
  "FORWARD_PERIOD_OF_SALES_AT_COST"
]);

/** Curated KPI set for the Compact view. Mirrors `FORECAST_COMPACT_VIEW_KPIS`. */
const COMPACT_VIEW_KPIS = new Set([
  "GS2",
  "GR2",
  "NS3_COGS",
  "INTAKE_AT_COST_TOTAL",
  "END_OF_PERIOD_AT_COST",
  "OPEN_TO_BUY_AT_COST",
  "FORWARD_PERIOD_OF_SALES_AT_COST"
]);

/** Non-per-sqm metrics that should still appear on the Per SQM view. */
const PER_SQM_VIEW_EXTRAS = new Set(["GS2", "GR2", "STORE_COUNT", "STORE_SQM"]);

/**
 * Predicate for the Per SQM view (`FORECAST_PER_SQM_VIEW_KPIS`): include
 * GS2/GR2/STORE_COUNT/STORE_SQM explicitly plus the plain client-computed
 * per-sqm metrics (`*_PER_SQM`). The server-computed `*_PER_SQM_LOCATION_AVG`
 * variant is deliberately excluded — it never appears on the summary, matching
 * Dash (see {@link isLocationAvgMetric}).
 */
function isPerSqmViewMetric(metric: string): boolean {
  return PER_SQM_VIEW_EXTRAS.has(metric) || isClientPerSqmMetric(metric);
}

/**
 * Whether a metric is a server-computed `*_PER_SQM_LOCATION_AVG` variant. These
 * are hidden on the summary grid in every view, matching the Dash app.
 */
function isLocationAvgMetric(metric: string): boolean {
  return metric.endsWith(`${PER_SQM_SUFFIX}_LOCATION_AVG`);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Suffix appended to a field name for its index column. */
export const INDEX_COL_SUFFIX = "|index";

// ---------------------------------------------------------------------------
// Formatters & helpers
// ---------------------------------------------------------------------------

/**
 * Returns a value formatter that formats numbers to a given decimal count
 * using en-US locale (with thousand separators).
 */
export function numberFormatter(decimals: number) {
  return function (params: { value: unknown }) {
    if (params.value == null) return "";
    const n = Number(params.value);
    if (Number.isNaN(n)) return "";
    return n.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };
}

/**
 * Determines the number of decimal places for a metric based on metadata.
 *
 * Priority:
 * 1. The decimals the metric's format string declares (`{:.2f}`, `{:.2%}`).
 * 2. "Price" category metrics default to 2; everything else defaults to 0.
 */
export function getDecimals(
  metricName: string,
  metaData?: ForecastMetaDataType
): number {
  const digits = formatDecimals(metricName, metaData?.metricFormatMapping);
  if (digits !== null) return digits;
  const cat = metaData?.metricCategoryMapping?.[metricName];
  return cat === "Price" ? 2 : 0;
}

/**
 * Returns the human-readable display label for a metric, falling back to the
 * raw metric name when no mapping is available.
 */
export function getDisplayLabel(
  metricName: string,
  metaData?: ForecastMetaDataType
): string {
  return metaData?.metricDisplayMapping?.[metricName] ?? metricName;
}

// ---------------------------------------------------------------------------
// Value getters
// ---------------------------------------------------------------------------

/**
 * Creates a valueGetter for IDX mode: (main / index) * 100.
 * Index columns use a "|index" suffix: "GS1|PTD" -> "GS1|PTD|index".
 */
export function indexValueGetter(field: string) {
  const indexField = `${field}${INDEX_COL_SUFFIX}`;
  return function (params: { data: Record<string, unknown> }) {
    const main = Number(params.data[field]);
    const idx = Number(params.data[indexField]);
    if (!idx || Number.isNaN(idx) || idx === 0) return null;
    if (Number.isNaN(main)) return null;
    return (main / idx) * 100;
  };
}

/**
 * valueSetter for IDX mode — the inverse of {@link indexValueGetter}. The user
 * edits the displayed index, so we convert it back to the absolute main value
 * (`main = index / 100 * idx`) and write it to `field`. The edit then
 * propagates identically to an absolute edit (MultiTableGrid forwards the
 * stored `field` value, not the re-derived index). Returns false — rejecting
 * the edit — when there's no index reference to invert against.
 */
export function indexValueSetter(field: string) {
  const indexField = `${field}${INDEX_COL_SUFFIX}`;
  return function (params: {
    data: Record<string, unknown>;
    newValue: unknown;
  }): boolean {
    const idx = Number(params.data[indexField]);
    const typed = Number(params.newValue);
    if (!idx || Number.isNaN(idx) || Number.isNaN(typed)) return false;
    params.data[field] = (typed / 100) * idx;
    return true;
  };
}

/**
 * `+/- Var` display: the same number {@link indexValueGetter} produces, minus
 * 100 — index 126 reads as a 26 % variance to the reference.
 */
export function varianceValueGetter(field: string) {
  const getIndex = indexValueGetter(field);
  return function (params: { data: Record<string, unknown> }) {
    const index = getIndex(params);
    return index === null ? null : index - INDEX_BASE;
  };
}

/**
 * valueParser for `+/- Var`. `percentUnitValueParser` reads a typed "26.00 %"
 * or a pasted 26; the guard on top absorbs `precision: 2`'s
 * truncation of the seeded value, which is an unrounded ratio here — without it
 * opening a cell and pressing Enter would write 26.33 over a stored 26.333.
 */
export function varianceValueParser(params: ValueParserParams) {
  const parsed = percentUnitValueParser(params);
  return keepUnlessEditorTruncation(params, parsed, VARIANCE_DECIMALS);
}

/**
 * valueSetter for `+/- Var` — the typed variance is an index of
 * `typed + 100`, then {@link indexValueSetter} inverts it to the absolute.
 */
export function varianceValueSetter(field: string) {
  const setIndex = indexValueSetter(field);
  return function (params: {
    data: Record<string, unknown>;
    newValue: unknown;
  }): boolean {
    const typed = Number(params.newValue);
    if (params.newValue == null || Number.isNaN(typed)) return false;
    return setIndex({ ...params, newValue: typed + INDEX_BASE });
  };
}

/**
 * Creates a valueGetter for SH% mode: (value / TTL) * 100.
 * The TTL field is "METRIC|TTL" in the same row.
 */
export function shareValueGetter(metric: string, aggField: string) {
  const ttlField = `${metric}|TTL`;
  return function (params: { data: Record<string, unknown> }) {
    const val = Number(params.data[aggField]);
    const ttl = Number(params.data[ttlField]);
    if (!ttl || Number.isNaN(ttl) || ttl === 0) return null;
    if (Number.isNaN(val)) return null;
    return (val / ttl) * 100;
  };
}

/**
 * Whether a metric is a plain per-SQM metric (`*_PER_SQM`) that the backend
 * ships as a null placeholder for client-side evaluation.
 *
 * Uses `endsWith` rather than `includes` so the server-computed
 * `*_PER_SQM_LOCATION_AVG` variant — which arrives with real values — is
 * excluded and keeps reading straight from the row data.
 */
export function isClientPerSqmMetric(metric: string): boolean {
  return metric.endsWith(PER_SQM_SUFFIX);
}

/**
 * Creates a valueGetter for a plain per-SQM column: base metric / STORE_SQM,
 * read from the same row and aggregation sub-column.
 *
 * Cell rendering, matching Dash:
 * - numerator missing → blank (no data)
 * - numerator is 0 → 0 (zero sales is zero sales/sqm, a real value)
 * - numerator non-zero but STORE_SQM missing/0 → blank (ratio is undefined)
 *
 * The per-SQM column carries no backend data (the server stopped shipping the
 * zero placeholders), so this getter is the sole source of the value. Only used
 * in ABS mode; index/share of a density ratio is not meaningful, so per-SQM
 * columns are hidden in IDX/SH% (see isMetricHidden).
 */
export function perSqmValueGetter(field: string) {
  const [metric, agg] = field.split("|");
  const baseField = `${metric.slice(0, -PER_SQM_SUFFIX.length)}|${agg}`;
  const sqmField = `${STORE_SQM_METRIC}|${agg}`;
  return function (params: { data: Record<string, unknown> }) {
    const numerator = params.data[baseField] as number | null | undefined;
    if (numerator == null) return null;
    if (numerator === 0) return 0;
    const sqm = params.data[sqmField] as number | null | undefined;
    if (!sqm) return null;
    return numerator / sqm;
  };
}

// ---------------------------------------------------------------------------
// Column definition builder
// ---------------------------------------------------------------------------

/** Options for {@link buildColumnDefs}. */
export interface BuildColumnDefsOptions {
  /** Metric field names to render as columns. */
  metrics: string[];
  /** Temporal aggregation view mode (PTD / PTG / TTL / ALL). */
  agg: AggView;
  /** Which aggregation periods are editable (e.g. `["PTD", "PTG"]`). */
  editableAggs: string[];
  /** Whether PTD values are mutable (future-year scenarios). */
  ptdMutable: boolean;
  /** Display mode: absolute values, index, variance, or share-of-total. */
  planBy: PlanByMode;
  /** Whether index reference data is available for the IDX / +/- Var modes. */
  hasIndexData: boolean;
  /** Forecast metadata used for formatting, labels, editability, and category mappings. */
  metaData?: ForecastMetaDataType;
  /**
   * The KPI columns a view seeds. `Value` / `Price` / `Units` filter by
   * `metricCategoryMapping`; `Compact` and `Per SQM` use curated KPI lists;
   * null shows every metric.
   */
  category?: SummaryView | null;
  /** Row dimension key shown in the pinned-left label column header. */
  rowDim?: string;
  /**
   * Per-metric visibility the user set by hand, over the seed. Keyed by
   * metric rather than colId so a choice survives an aggregation switch,
   * which renames every column. The structural rules in `isMetricHidden`
   * (excluded metrics, per-SQM outside ABS) still win.
   */
  visibility?: Record<string, boolean>;
}

/**
 * Returns whether a cell is editable for the given metric and aggregation.
 *
 * Rules:
 * - SH% mode -> not editable (share-of-total is display-only). IDX mode edits
 *   like ABS: the index is just a display of the same value (see indexValueSetter).
 * - TTL -> editable if at least one of PTD/PTG is editable
 * - PTD -> only in future years (ptdMutable)
 * - PTG -> editable in current/future years
 * - Metric must be marked editable in KPI definitions, and not be one the
 *   summary cannot recalculate (target stock, FPOS)
 */
function isCellEditable(
  metric: string,
  a: string,
  options: BuildColumnDefsOptions
): boolean {
  const { planBy, editableAggs, metaData } = options;
  if (planBy === "SH%") return false;
  if (SUMMARY_READ_ONLY_METRICS.has(metric)) return false;
  if (a === "TTL") {
    if (editableAggs.length === 0) return false;
  } else if (!editableAggs.includes(a)) {
    return false;
  }
  // The summary save persists sales only (MIGRATION.md §5.1).
  if (INTAKE_COMPONENT_METRICS.has(metric)) return false;
  const metricEditable = metaData?.metricEditableMapping?.[metric];
  if (metricEditable === false) return false;
  return true;
}

function isMetricHidden(
  metric: string,
  options: BuildColumnDefsOptions
): boolean {
  const { category, planBy, metaData, visibility } = options;
  if (EXCLUDED_METRICS.has(metric)) return true;
  if (AGGREGATED_UNSAFE_METRICS.has(metric)) return true;
  // Location-avg per-SQM columns never appear on the summary, matching Dash.
  if (isLocationAvgMetric(metric)) return true;
  // Plain per-SQM columns are a client-side density ratio with no backend data;
  // index/share of them isn't meaningful, so they only show in ABS mode.
  if (planBy !== "ABS" && isClientPerSqmMetric(metric)) return true;
  // Past the structural rules, a hand-set choice beats the view's seed.
  const chosen = visibility?.[metric];
  if (chosen !== undefined) return !chosen;
  // OPTION_ columns show only when opened from the column menu (above).
  if (metric.startsWith("OPTION_")) return true;
  if (category === "Compact") return !COMPACT_VIEW_KPIS.has(metric);
  if (category === "Per SQM") return !isPerSqmViewMetric(metric);
  // Outside the Per SQM view, plain per-SQM columns are never shown.
  if (isClientPerSqmMetric(metric)) return true;
  if (!category || !metaData?.metricCategoryMapping) return false;
  const metricCat = metaData.metricCategoryMapping[metric];
  if (!metricCat) return false;
  return metricCat !== category;
}

/** Applies planBy-specific overrides (IDX / +/- Var / SH%) to a base column def. */
function applyPlanByOverrides(
  base: ColDef,
  metric: string,
  field: string,
  options: BuildColumnDefsOptions
): ColDef {
  const { planBy, hasIndexData, metaData } = options;
  // Plain per-SQM columns are computed client-side from base / STORE_SQM. The
  // column has no backend data, so IDX/SH% have nothing to operate on — the
  // per-SQM getter wins in every mode. Decimals follow the BASE metric (e.g.
  // GS1 → integer), matching Dash, rather than the per-SQM metric's own format.
  if (isClientPerSqmMetric(metric)) {
    const baseMetric = metric.slice(0, -PER_SQM_SUFFIX.length);
    return {
      ...base,
      editable: false,
      cellRenderer: undefined,
      valueGetter: perSqmValueGetter(field),
      valueFormatter: numberFormatter(getDecimals(baseMetric, metaData))
    };
  }
  if (isIndexFamilyMode(planBy) && hasIndexData) {
    // editable + the ChangeFlashCell (from base) are preserved:
    // index is a display of the same value, so the same cells editable in ABS
    // are editable here (see indexValueSetter), and the cascade's up/down change
    // arrows must flash on every affected index cell just like in ABS.
    if (planBy === "IDX") {
      return {
        ...base,
        valueGetter: indexValueGetter(field),
        valueSetter: indexValueSetter(field),
        valueFormatter: numberFormatter(0)
      };
    }
    // "percentUnit" carries the "26.00 %" label; the editor caps entry at those
    // 2 decimals and `varianceValueParser` absorbs the truncation that implies.
    return {
      ...base,
      type: ["numericColumn", "percentUnit"],
      valueGetter: varianceValueGetter(field),
      valueSetter: varianceValueSetter(field),
      valueParser: varianceValueParser,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: { precision: VARIANCE_DECIMALS }
    };
  }
  if (planBy === "SH%") {
    return {
      ...base,
      editable: false,
      cellRenderer: undefined,
      valueGetter: shareValueGetter(metric, field),
      valueFormatter: numberFormatter(2)
    };
  }
  if (isPercentageMetric(metric, metaData?.metricFormatMapping)) {
    // Percentage fractions display as "60.54 %" here exactly as they do on the
    // temporal grid, and editable ones seed the editor with that same label.
    const decimals = percentageDecimals(metric, metaData?.metricFormatMapping);
    return {
      ...base,
      valueFormatter: (params) => percentFormatter(params, decimals),
      cellEditor: "agTextCellEditor",
      cellEditorParams: { useFormatter: true },
      valueParser: (params) => percentScaledValueParser(params, decimals)
    };
  }
  return {
    ...base,
    valueFormatter: numberFormatter(getDecimals(metric, metaData))
  };
}

/**
 * Builds AG Grid column definitions for the forecast summary table.
 *
 * Handles ABS / IDX / +/- Var / SH% plan-by modes, per-metric editability, category
 * filtering, and the ALL aggregation view (grouped PTD/PTG/TTL children).
 */
export function buildColumnDefs(
  options: BuildColumnDefsOptions
): (ColDef | ColGroupDef)[] {
  const { metrics, agg, metaData, rowDim } = options;

  const rowLabelCol: ColDef = {
    field: "row_label",
    headerName: rowDim ? rowDim.replace(/_/g, " ").toUpperCase() : "",
    pinned: "left",
    minWidth: 150,
    suppressSizeToFit: true
  };

  const metricCols: (ColDef | ColGroupDef)[] = metrics.map((metric) => {
    const label = getDisplayLabel(metric, metaData);

    if (agg === "ALL") {
      return {
        headerName: label,
        children: (["PTD", "PTG", "TTL"] as const).map((a) => {
          const field = `${metric}|${a}`;
          const base: ColDef = {
            field,
            headerName: a,
            editable: isCellEditable(metric, a, options),
            hide: isMetricHidden(metric, options),
            cellRenderer: ChangeFlashCell,
            type: "numericColumn"
          };
          return applyPlanByOverrides(base, metric, field, options);
        })
      } satisfies ColGroupDef;
    }

    const field = `${metric}|${agg}`;
    const base: ColDef = {
      field,
      headerName: label,
      editable: isCellEditable(metric, agg, options),
      hide: isMetricHidden(metric, options),
      cellRenderer: ChangeFlashCell,
      type: "numericColumn"
    };
    return applyPlanByOverrides(base, metric, field, options);
  });

  return [rowLabelCol, ...metricCols];
}

// ---------------------------------------------------------------------------
// Version selector helper
// ---------------------------------------------------------------------------

/**
 * Builds select options from version data for a given producer type.
 */
export function getVersionOptions(
  producer: string,
  versionsData?: {
    user_versions?: { label: string; cube_id: string }[];
    other_user_versions?: { label: string; cube_id: string }[];
    ds_versions?: { label: string; cube_id: string }[];
  },
  budgetVersionsData?: {
    budget_versions?: { label: string; cube_id: string }[];
  }
) {
  switch (producer) {
    case "user":
      return (versionsData?.user_versions ?? []).map((v) => ({
        label: v.label,
        value: v.cube_id
      }));
    case "other_user":
      return (versionsData?.other_user_versions ?? []).map((v) => ({
        label: v.label,
        value: v.cube_id
      }));
    case "analytics":
      return (versionsData?.ds_versions ?? []).map((v) => ({
        label: v.label,
        value: v.cube_id
      }));
    case "budget":
      return (budgetVersionsData?.budget_versions ?? []).map((v) => ({
        label: v.label,
        value: v.cube_id
      }));
    default:
      return [];
  }
}
